/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../../theme/theme"
import { css } from "@mui/material"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Slider, Box } from "@mui/material"
import { useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import Cropper from "react-easy-crop"
import { Area } from "react-easy-crop/types"

const DialogTitleStyle = withMyTheme((theme) => css`
    font-size: 1.5rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
`)

const CropContainerStyle = withMyTheme((theme) => css`
    position: relative;
    width: 100%;
    height: 400px;
    background-color: ${theme.palette.grey[900]};
`)

const ControlsStyle = withMyTheme((theme) => css`
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`)

interface ImageCropDialogProps {
    open: boolean
    image: string
    onClose: () => void
    onCropComplete: (croppedImage: string) => void
}

const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.addEventListener('load', () => resolve(image))
        image.addEventListener('error', (error) => reject(error))
        image.src = url
    })

const getCroppedImg = async (imageSrc: string, pixelCrop: Area): Promise<string> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
        throw new Error('No 2d context')
    }

    // Set canvas size to match the cropped area
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height

    // Draw the cropped image
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    )

    // Return as base64
    return canvas.toDataURL('image/jpeg')
}

export const ImageCropDialog = ({ open, image, onClose, onCropComplete }: ImageCropDialogProps) => {
    const { t } = useTranslation()
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

    const onCropChange = useCallback((crop: { x: number; y: number }) => {
        setCrop(crop)
    }, [])

    const onZoomChange = useCallback((zoom: number) => {
        setZoom(zoom)
    }, [])

    const onCropCompleteCallback = useCallback(
        (croppedArea: Area, croppedAreaPixels: Area) => {
            setCroppedAreaPixels(croppedAreaPixels)
        },
        []
    )

    const handleCrop = async () => {
        if (croppedAreaPixels) {
            const croppedImage = await getCroppedImg(image, croppedAreaPixels)
            onCropComplete(croppedImage)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle css={DialogTitleStyle}>
                {t('questionnaire.witnesses.cropTitle')}
            </DialogTitle>
            <DialogContent>
                <Box css={CropContainerStyle}>
                    <Cropper
                        image={image}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        onCropChange={onCropChange}
                        onZoomChange={onZoomChange}
                        onCropComplete={onCropCompleteCallback}
                    />
                </Box>
                <Box css={ControlsStyle}>
                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(e, zoom) => setZoom(zoom as number)}
                        aria-labelledby="Zoom"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    {t('questionnaire.witnesses.cancelButton')}
                </Button>
                <Button onClick={handleCrop} variant="contained">
                    {t('questionnaire.witnesses.cropButton')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
